<script setup lang="ts">
import { computed, ref } from 'vue';
import type { HeroProfile, EffectiveStats } from '../game/hero-api';
import type { Inventory } from '../game/inventory-api';
import { STAT_META } from '../game/item-math';
import { CLASS_ABILITY, UNIVERSAL_MOVES } from '../game/class-ability';
import { t } from '../i18n';

/**
 * DOSSIER — the right column. A rune-scored saga-record of the hero's
 * combat totals, paperdoll, set bonuses, class signature move and active
 * boosts. Purely a display of stats/inventory props owned by HeroScreen.
 */
const props = defineProps<{ hero: HeroProfile; stats: EffectiveStats | null; inventory: Inventory | null }>();

const tiles = computed(() => {
  const eff = props.stats?.effective; const base = props.stats?.base; const gear = props.stats?.gear;
  const h = props.hero.hero.stats;
  const combat = (['attack', 'defense', 'magic', 'speed'] as const).map((k) => ({
    key: k,
    label: STAT_META[k].label,
    color: STAT_META[k].color,
    desc: STAT_META[k].desc,
    total: eff?.[k] ?? h[k],
    base: base?.[k] ?? h[k],
    gear: k === 'speed' ? (gear?.speed ?? 0) : (gear?.[k] ?? 0),
  }));
  const health = {
    key: 'hp' as const, label: t('heroDossier.health'), color: 'var(--color-success)',
    desc: t('heroDossier.healthDesc'),
    total: eff?.maxHp ?? h.maxHp, base: base?.maxHp ?? h.maxHp, gear: gear?.hp ?? 0,
  };
  const nerve = {
    key: 'nerve' as const, label: t('heroDossier.fury'), color: 'var(--color-gold)',
    desc: t('heroDossier.furyDesc'),
    total: props.hero.hero.nerve.current, base: props.hero.hero.nerve.current, gear: 0,
    sub: t('heroDossier.ofMax', { max: props.hero.hero.nerve.max }),
  };
  return [...combat, health, nerve];
});

// QA punch-list 07.08 item 6 — "what does each stat do?": every tile answers on
// tap (works on touch, where a title-tooltip does not exist) into a visible
// info field under the grid; hover still gets the native tooltip for free.
const explained = ref<string | null>(null);
const explainedTile = computed(() => tiles.value.find((t) => t.key === explained.value) ?? null);
function explain(key: string) {
  explained.value = explained.value === key ? null : key;
}

const ability = computed(() => CLASS_ABILITY[props.hero.hero.class]);

// Maneuvers are paged one-at-a-time (dots) so the ability card keeps a fixed
// height — no scroll no matter how long the move descriptions run.
const moveIdx = ref(0);
const move = computed(() => UNIVERSAL_MOVES[moveIdx.value]);
</script>

<template>
  <!-- THE RECKONING — a full-width strip beneath the kit and the stash. Was the
       profile's third COLUMN, a vertical stack of small panels; three columns of
       stacked cards is the browser-RPG stat sheet, and the shape was the tell. -->
  <div class="hd-strip">
    <!-- STAT TILES -->
    <div class="hd-panel bound-frame relative border border-border p-3">
                  <div class="hd-h mb-2">{{ t('heroDossier.title') }}</div>
      <div class="hd-tiles">
        <button
          v-for="t in tiles" :key="t.key" class="hd-tile"
          :class="{ 'is-explained': explained === t.key }"
          type="button"
          :title="t.label + ' — ' + t.desc"
          :aria-expanded="explained === t.key"
          @click="explain(t.key)"
        >
          <span class="hd-tile-underline" :style="{ background: `linear-gradient(90deg, ${t.color}, transparent)` }"></span>
          <div class="hd-tile-label">{{ t.label }} <i class="hd-tile-q" aria-hidden="true">?</i></div>
          <div class="hd-tile-total" :style="{ color: t.color }">{{ t.total.toLocaleString() }}</div>
          <div v-if="t.gear" class="hd-tile-break">{{ t.base }} <span class="text-gold">+{{ t.gear }}</span></div>
          <div v-else-if="'sub' in t" class="hd-tile-break">{{ (t as { sub: string }).sub }}</div>
        </button>
      </div>
      <!-- the info field: what the tapped stat actually does, in plain words -->
      <div class="hd-explain" :class="{ 'is-live': explainedTile }" aria-live="polite">
        <template v-if="explainedTile">
          <b :style="{ color: explainedTile.color }">{{ explainedTile.label }}.</b> {{ explainedTile.desc }}
        </template>
        <template v-else>{{ t('heroDossier.tapHint') }}</template>
      </div>
    </div>

    <!-- The 6-across "Loadout" grid that used to sit here is gone. It was a
         SECOND copy of the same six equipment slots already shown on the hero
         panel — and it was never a paperdoll despite the class name, just another
         row. The real paperdoll now lives on the hero panel, with each slot placed
         where the gear actually goes (see SLOT_ANATOMY in HeroCard.vue). -->

    <!-- SET BONUSES -->
    <div class="hd-panel bound-frame relative border border-border p-3">
                  <div class="hd-h mb-2.5">{{ t('heroDossier.setBonuses') }}</div>
      <template v-if="stats?.sets.length">
        <div v-for="s in stats.sets" :key="s.name" class="mb-2 last:mb-0 flex items-start gap-2">
          <span class="hd-dot"></span>
          <div>
            <div class="font-heading text-gold capitalize text-sm">{{ s.name }} <span class="text-text-dim font-body text-xs">· {{ s.pieces }}{{ t('heroDossier.pc') }}</span></div>
            <div class="font-body text-xs text-text-dim italic leading-snug">{{ s.description }}</div>
          </div>
        </div>
      </template>
      <p v-else class="font-body text-xs text-text-dim italic">{{ t('heroDossier.setBonusEmpty') }}</p>
    </div>

    <!-- CLASS ABILITY -->
    <div class="hd-panel bound-frame relative border border-border p-3">
                  <div class="hd-h mb-2.5">{{ t('heroDossier.classAbility') }}</div>
      <div class="font-heading text-gold-bright text-base">{{ ability.name }}</div>
      <div class="font-body text-xs text-text-dim mb-2 hd-ability-desc">{{ ability.desc }} <span class="text-gold">· {{ t('heroDossier.cooldown', { n: ability.cooldown }) }}</span></div>
      <div class="border-t border-border pt-2">
        <div class="flex items-center justify-between mb-1.5">
          <span class="hd-h">{{ t('heroDossier.maneuvers') }}</span>
          <span class="hd-pager">
            <button
              v-for="(m, i) in UNIVERSAL_MOVES" :key="m.name"
              class="hd-pdot" :class="{ on: i === moveIdx }"
              :title="m.name" :aria-label="m.name" @click="moveIdx = i"
            ></button>
          </span>
        </div>
        <div class="hd-move">
          <span class="font-label uppercase tracking-wider text-gold text-[11px]">{{ move.name }}</span>
          <p class="font-body text-[11px] text-text-dim leading-snug mt-0.5">{{ move.desc }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.hd-h {
  font-family: var(--font-label, sans-serif);
  text-transform: uppercase; letter-spacing: 0.08em;
  font-size: 11px; color: var(--color-text-dim, #8a8378);
}
.hd-tile {
  position: relative; overflow: hidden;
  border: 0;
  border-radius: 0;
  clip-path: var(--clip-hewn);
  padding: 0.5rem 0.65rem 0.55rem;
  background: linear-gradient(180deg, #0b0f14, #12171f 60%, #0a0e13);
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.8), inset 0 -1px 0 rgba(226, 236, 248, 0.06);
}
.hd-tile-underline { position: absolute; left: 0; right: 0; bottom: 0; height: 2px; }
.hd-tile-label { font-family: var(--font-label, sans-serif); text-transform: uppercase; letter-spacing: 0.07em; font-size: 9.5px; color: var(--color-text-dim, #8a8378); }
.hd-tile-total { font-family: var(--font-heading, serif); font-size: 1.2rem; line-height: 1.15; font-variant-numeric: tabular-nums; }
/* the strip: three planks abreast, stacking on narrow screens. The stat tiles get
   the widest share because they hold six numbers. */
.hd-strip {
  display: grid;
  grid-template-columns: 1.5fr 1fr 1.2fr;
  gap: 0.85rem;
  align-items: start;
  min-height: 0;
}
@media (max-width: 1100px) { .hd-strip { grid-template-columns: 1fr; } }
.hd-panel {
  border-radius: 0;
  background: var(--surface-plank);
  background-size: var(--surface-plank-size);
  background-blend-mode: var(--surface-plank-blend);
}
/* six totals across on a wide strip, two-up when it stacks */
.hd-tiles { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0.6rem; }
@media (max-width: 700px) { .hd-tiles { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
.hd-tile-break { font-family: var(--font-body, serif); font-size: 10.5px; color: var(--color-text-dim, #8a8378); }
/* stat tiles answer on tap — the button reset keeps the carved plank look */
.hd-tile { text-align: left; cursor: pointer; font: inherit; color: inherit; border: 0; width: 100%; }
.hd-tile:focus-visible { outline: 2px solid var(--color-gold, #c9a84c); outline-offset: 2px; }
.hd-tile.is-explained { box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.8), inset 0 0 0 1px rgba(201, 168, 76, 0.55), inset 0 -1px 0 rgba(226, 236, 248, 0.06); }
/* the "there is an answer here" chip — a proper stamped ring, not a floating
   superscript (owner: the info icons were too small to read as tappable) */
.hd-tile-q {
  display: inline-grid; place-items: center; margin-left: 5px;
  width: 15px; height: 15px; font-style: normal;
  font-size: 10px; font-weight: 700; line-height: 1; vertical-align: -2px;
  color: var(--color-gold-bright, #e8d9a8);
  background: linear-gradient(180deg, #232b39, #141a24);
  border-radius: 50%;
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--color-gold, #c9a84c) 45%, transparent), inset 0 1px 0 rgba(226, 236, 248, 0.1);
}
.hd-tile.is-explained .hd-tile-q,
.hd-tile:hover .hd-tile-q {
  color: #10151d;
  background: linear-gradient(180deg, var(--color-gold-bright, #e8d9a8), var(--color-gold, #c9a84c));
}
.hd-explain {
  margin-top: 0.6rem; padding: 0.5rem 0.65rem;
  font-family: var(--font-body, serif); font-size: 12px; font-style: italic;
  line-height: 1.45; color: var(--color-text-dim, #8a8378);
  background: linear-gradient(180deg, #0b0f14, #10151d);
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.7), inset 0 -1px 0 rgba(226, 236, 248, 0.05);
}
.hd-explain.is-live { color: #c9d4e2; font-style: normal; }
.hd-explain b { font-family: var(--font-heading, serif); font-style: normal; }

@keyframes hd-tip-in { from { opacity: 0; } }
.hd-dot { width: 6px; height: 6px; margin-top: 5px; border-radius: 0; background: var(--color-gold, #c9a84c); box-shadow: 0 0 6px var(--color-gold, #c9a84c); flex-shrink: 0; }
/* Class-ability maneuver pager — one move per page, fixed height (no scroll) */
.hd-ability-desc {
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  overflow: hidden;
}
.hd-pager { display: inline-flex; gap: 6px; align-items: center; }
.hd-pdot {
  width: 7px; height: 7px; padding: 0; border-radius: 0; cursor: pointer;
  border: 1px solid color-mix(in srgb, var(--color-gold, #c9a84c) 55%, transparent);
  background: transparent; transition: background 130ms ease, box-shadow 130ms ease;
}
.hd-pdot.on { background: var(--color-gold, #c9a84c); box-shadow: 0 0 6px color-mix(in srgb, var(--color-gold, #c9a84c) 60%, transparent); }
.hd-pdot:hover { border-color: var(--color-gold, #c9a84c); }
.hd-move { min-height: 42px; }
</style>
